/**
 * IntegrationCatalogGrid — renders the integration manifest as a grid
 * of "available to connect" tiles.
 *
 * Used by:
 *   - `OrgIntegrationsPage` (tier=ORG, orgId=activeOrg.id)
 *   - `PersonalConnectionsPage` (tier=USER)
 *   - the JIT step Connect picker (tier=RUN)
 *
 * The grid hides integrations the caller already has an active
 * connection for, by way of the `connectedIntegrationIds` prop —
 * keeps the catalog focused on "what can I add now?" without
 * duplicating the existing-connections list above.
 *
 * Catalog data comes from `GET_INTEGRATION_CATALOG`, which proxies
 * the manifest in `firebase/functions/src/connectors/platform/integrations.yaml`.
 * Token material never rides on that query (M2 trust boundary,
 * ADR-0002).
 */

import React, {useMemo} from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';
import {useQuery} from '@apollo/client';
import {GET_INTEGRATION_CATALOG} from '../../graphql/queries';
import {ConnectionTier} from '../../types/generated/graphql';
import {ConnectButton} from './ConnectButton';
import {IntegrationLogo, getBrandColor} from '../../integrations';

interface CatalogEntry {
  id: string;
  provider: string;
  name: string;
  category: string;
  description: string;
  priority: string;
  authType: string;
  allowedTiers: ConnectionTier[];
  defaultTier: ConnectionTier;
  capabilities: string[];
  industries: string[];
}

interface CatalogQueryResult {
  integrationCatalog: CatalogEntry[];
}

interface Props {
  tier: ConnectionTier;
  orgId?: string;
  runId?: string;
  /**
   * Integration ids the caller already has an active (non-revoked,
   * non-disconnected) connection for, at this tier. Used to hide
   * those entries from the "available" grid.
   */
  connectedIntegrationIds?: string[];
  /** Optional category filter ("crm", "esign", "calendar", ...). */
  category?: string;
  /** Display title above the grid. Defaults to "Available integrations". */
  title?: string;
}

const PRIORITY_RANK: Record<string, number> = {P0: 0, P1: 1, P2: 2, P3: 3};

export const IntegrationCatalogGrid: React.FC<Props> = ({
  tier,
  orgId,
  runId,
  connectedIntegrationIds,
  category,
  title = 'Available integrations',
}) => {
  const {data, loading, error} = useQuery<CatalogQueryResult>(
    GET_INTEGRATION_CATALOG,
    {variables: {tier}, fetchPolicy: 'cache-and-network'},
  );

  const visible = useMemo(() => {
    if (!data?.integrationCatalog) return [];
    const connected = new Set(connectedIntegrationIds ?? []);
    return data.integrationCatalog
      .filter((entry) => !connected.has(entry.id))
      .filter((entry) => (category ? entry.category === category : true));
  }, [data, connectedIntegrationIds, category]);

  const grouped = useMemo(() => {
    const byCat = new Map<string, CatalogEntry[]>();
    for (const entry of visible) {
      const list = byCat.get(entry.category) ?? [];
      list.push(entry);
      byCat.set(entry.category, list);
    }
    byCat.forEach((list) => {
      list.sort((a, b) => {
        const pa = PRIORITY_RANK[a.priority] ?? 99;
        const pb = PRIORITY_RANK[b.priority] ?? 99;
        if (pa !== pb) return pa - pb;
        return a.name.localeCompare(b.name);
      });
    });
    return Array.from(byCat.entries()).sort((a: [string, CatalogEntry[]], b: [string, CatalogEntry[]]) =>
      a[0].localeCompare(b[0]),
    );
  }, [visible]);

  if (loading && !data) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{mb: 2}}>
        Could not load integration catalog: {error.message}
      </Alert>
    );
  }

  if (visible.length === 0) {
    return (
      <Alert severity="info" sx={{mb: 2}}>
        All integrations available at this tier are already connected.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 1}}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
        {visible.length} {visible.length === 1 ? 'integration' : 'integrations'}{' '}
        available to connect at this tier.
      </Typography>

      {grouped.map(([cat, entries]) => (
        <Box key={cat} sx={{mb: 3}}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{fontWeight: 600, letterSpacing: 1, display: 'block', mb: 1}}
          >
            {cat}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 2,
            }}
          >
            {entries.map((entry) => {
              const brandColor = getBrandColor(entry.id);
              return (
              <Card
                key={entry.id}
                variant="outlined"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  borderTop: brandColor
                    ? `3px solid ${brandColor}`
                    : undefined,
                }}
              >
                <CardContent sx={{flexGrow: 1, display: 'flex', flexDirection: 'column'}}>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{mb: 1}}>
                    <IntegrationLogo
                      integrationId={entry.id}
                      name={entry.name}
                      size={32}
                    />
                    <Typography variant="subtitle1" sx={{fontWeight: 600, flexGrow: 1}}>
                      {entry.name}
                    </Typography>
                    {entry.priority === 'P0' && (
                      <Chip label="Core" size="small" color="primary" />
                    )}
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{mb: 2, flexGrow: 1, minHeight: 40}}
                  >
                    {entry.description}
                  </Typography>
                  <Stack direction="row" spacing={0.5} sx={{mb: 2, flexWrap: 'wrap', gap: 0.5}}>
                    {entry.allowedTiers.map((t) => (
                      <Chip
                        key={t}
                        label={t}
                        size="small"
                        variant={t === tier ? 'filled' : 'outlined'}
                        color={t === tier ? 'primary' : 'default'}
                      />
                    ))}
                  </Stack>
                  <ConnectButton
                    integrationId={entry.id}
                    tier={tier}
                    orgId={orgId}
                    runId={runId}
                    label={`Connect ${entry.name}`}
                    fullWidth
                  />
                </CardContent>
              </Card>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
};
